// api/providers/moviesFavorites.ts
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesMovies } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

const favoritesCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

// ✅ ADDED: Helper to get current playlist identifier
const getCurrentPlaylistId = () => {
  return `${playListUrl()}_${playListUsername()}`;
};

// ✅ MODIFIED: Filter by current playlist
const getFavoriteMovies = () => {
  try {
    const data = localStorage.getItem("movie_favorites");
    if (!data) return [];

    const allFavorites = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    // Filter only favorites from current playlist
    const playlistFavorites = allFavorites.filter(item => item.playlistId === currentPlaylist);

    return playlistFavorites.sort((a, b) => b.addedTimestamp - a.addedTimestamp);
  } catch (error) {
    console.error("Error reading movie favorites:", error);
    return [];
  }
};

const buildApiUrl = async (baseUrl, username, password, action, additionalParams = "") => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;
  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  let finalApiUrl = apiUrl;
  try {
    const dnsResult = await dnsResolver.transformUrl(apiUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
    }
  } catch (dnsError) {
    console.warn("DNS transformation failed:", dnsError);
  }
  return finalApiUrl;
};

export default function (filter) {
  return async pageIndex => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Movie Favorites");
      return [];
    }

    const cacheKey = `${baseUrl}_${username}_movie_favorites`;

    const cached = favoritesCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log("Using cached movie favorites data");
      return cached.data;
    }

    console.log("Loading Movie Favorites...");

    try {
      const favoritesList = getFavoriteMovies();

      if (favoritesList.length === 0) {
        console.log("No favorite movies - returning empty array");
        return [];
      }

      console.log(`Found ${favoritesList.length} favorite movies`);

      const streamIds = new Set();
      const categoryIds = new Set();

      favoritesList.forEach(item => {
        if (item.stream_id) streamIds.add(item.stream_id);
        if (item.category_id) categoryIds.add(item.category_id);
      });

      const allMovies = [];

      for (const categoryId of categoryIds) {
        try {
          const categoryUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_vod_streams",
            `category_id=${categoryId}`,
          );
          const movies = await fetchData(categoryUrl);
          if (Array.isArray(movies)) {
            allMovies.push(...movies);
          }
        } catch (error) {
          console.warn(`Failed to fetch movie category ${categoryId}:`, error);
        }
      }

      const matchedMovies = [];

      for (const favItem of favoritesList) {
        try {
          const movieData = allMovies.find(m => String(m.stream_id) === String(favItem.stream_id));

          if (movieData) {
            matchedMovies.push({
              ...movieData,
              favorite_timestamp: favItem.addedTimestamp,
            });
          } else {
            matchedMovies.push({
              stream_id: favItem.stream_id,
              name: favItem.name || `Movie ${favItem.stream_id}`,
              stream_icon: favItem.poster || "",
              category_id: favItem.category_id || "unknown",
              rating_5based: favItem.rating || 0,
              favorite_timestamp: favItem.addedTimestamp,
            });
          }
        } catch (err) {
          console.warn("Error processing favorite movie:", err);
        }
      }

      const limitedMovies = matchedMovies.slice(0, 50);
      const processedTiles = convertItemsToTilesMovies(limitedMovies);

      favoritesCache.set(cacheKey, {
        data: processedTiles,
        timestamp: Date.now(),
      });

      console.log(`Movie favorites processed: ${processedTiles.length} tiles`);
      return processedTiles;
    } catch (error) {
      console.error("Error getting Movie Favorites:", error);
      return [];
    }
  };
}

// ✅ MODIFIED: Add playlistId when saving
export const addMovieToFavorites = movieData => {
  try {
    const data = localStorage.getItem("movie_favorites");
    let favoritesList = data ? JSON.parse(data) : [];

    const currentPlaylist = getCurrentPlaylistId();

    const existingIndex = favoritesList.findIndex(
      item => String(item.stream_id) === String(movieData.stream_id) && item.playlistId === currentPlaylist,
    );

    if (existingIndex !== -1) {
      console.log("Movie already in favorites");
      return false;
    }

    const entry = {
      stream_id: movieData.stream_id,
      name: movieData.name || "",
      poster: movieData.poster || "",
      category_id: movieData.category_id || "",
      rating: movieData.rating || 0,
      addedTimestamp: Date.now(),
      playlistId: currentPlaylist, // ✅ ADDED THIS LINE
    };

    favoritesList.push(entry);

    if (favoritesList.length > 100) {
      favoritesList = favoritesList.slice(-100);
    }

    localStorage.setItem("movie_favorites", JSON.stringify(favoritesList));
    favoritesCache.clear();

    console.log("Added movie to favorites:", movieData.name);
    return true;
  } catch (error) {
    console.error("Error adding movie to favorites:", error);
    return false;
  }
};

// ✅ MODIFIED: Remove only from current playlist
export const removeMovieFromFavorites = streamId => {
  try {
    const data = localStorage.getItem("movie_favorites");
    if (!data) return false;

    let favoritesList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();
    const initialLength = favoritesList.length;

    // Remove only from current playlist
    favoritesList = favoritesList.filter(
      item => !(String(item.stream_id) === String(streamId) && item.playlistId === currentPlaylist),
    );

    if (favoritesList.length === initialLength) {
      console.log("Movie not found in favorites");
      return false;
    }

    localStorage.setItem("movie_favorites", JSON.stringify(favoritesList));
    favoritesCache.clear();

    console.log("Removed movie from favorites");
    return true;
  } catch (error) {
    console.error("Error removing movie from favorites:", error);
    return false;
  }
};

// ✅ MODIFIED: Check only in current playlist
export const isMovieFavorite = streamId => {
  try {
    const data = localStorage.getItem("movie_favorites");
    if (!data) return false;

    const favoritesList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    return favoritesList.some(
      item => String(item.stream_id) === String(streamId) && item.playlistId === currentPlaylist,
    );
  } catch (error) {
    console.error("Error checking if movie is favorite:", error);
    return false;
  }
};

export const clearMovieFavorites = () => {
  localStorage.removeItem("movie_favorites");
  favoritesCache.clear();
  console.log("Cleared all movie favorites");
};

// ✅ MODIFIED: Check only current playlist
export const hasAnyFavoriteMovies = () => {
  try {
    const data = localStorage.getItem("movie_favorites");
    if (!data) return false;

    const list = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    return list.some(item => item.playlistId === currentPlaylist);
  } catch {
    return false;
  }
};
